import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { Screen } from './Frame'
import { useStepFooter } from './hooks'
import { useFirstRun } from './store'
import { ALL_STAGES_AND_TAGS, AREA_DIRECTORY, DEMO_BUSINESS, FF_NUMBER, FF_NUMBER_E164, LEAD_AREAS, STOP_QUESTIONS, STOP_SUGGESTIONS, TStopGroup } from './data'
import { Chip, Icon, Input, LinkButton, SearchPick, Select } from './ui'
import { RowCard } from './steps-a'

/* ---------- 6. Area code ---------- */

export const AreaStep = () => {
   const { areaCodes, set, next, direction } = useFirstRun()
   const [adding, setAdding] = useState(false)
   const total = LEAD_AREAS.reduce((n, a) => n + a.leads, 0)
   const top = LEAD_AREAS[0]
   const toggle = (code: string) => {
      if (areaCodes.includes(code)) {
         if (areaCodes.length > 1) set('areaCodes', areaCodes.filter((c) => c !== code))
      } else if (areaCodes.length < 3) set('areaCodes', [...areaCodes, code])
   }
   const label = (code: string) => {
      const a = AREA_DIRECTORY.find((x) => x.code === code)
      return a ? `${a.city}, ${a.state}` : 'Added by you'
   }
   useStepFooter(`Use ${areaCodes.map((c) => `(${c})`).join(' and ')}`, next, { hint: 'Numbers are bought when your first campaign starts. The first three are included.', testId: 'confirm-area' })
   const options = AREA_DIRECTORY.filter((a) => !areaCodes.includes(a.code)).map((a) => ({ id: a.code, label: `(${a.code}) ${a.city}, ${a.state}`, sub: LEAD_AREAS.find((l) => l.code === a.code) ? `${LEAD_AREAS.find((l) => l.code === a.code)!.leads} of your leads` : undefined }))
   return (
      <Screen stepKey="area" direction={direction} kind="area" title="Your leads will see calls from a local number" lede={`${Math.round((top.leads / total) * 100)}% of your leads are around ${top.city}, so we suggest a (${top.code}) area code. Leads pick up local numbers far more often. Pick up to three.`} width="sm">
         <RowCard testId="area-card">
            {LEAD_AREAS.slice(0, 3).map((a) => {
               const on = areaCodes.includes(a.code)
               return (
                  <button key={a.code} type="button" onClick={() => toggle(a.code)} data-testid={`code-${a.code}`} className={cn('w-full flex items-center gap-4 px-5 min-h-[64px] text-left transition-colors', on ? 'bg-[var(--fr-blue-tint)]/60' : 'hover:bg-[var(--fr-surface-2)]')}>
                     <span className={cn('flex shrink-0 items-center justify-center w-10 h-10 rounded-full', on ? 'bg-[var(--fr-blue)] text-white' : 'bg-[var(--fr-surface-2)] text-[var(--fr-text)]')}>{on ? <Icon name="check" size={16} /> : <Icon name="pin" size={16} />}</span>
                     <span className="flex-1 min-w-0">
                        <span className="block text-[15px] font-semibold">({a.code}) · {a.city}, {a.state}</span>
                        <span className="block text-[13px] text-[var(--fr-text-2)]">{a.leads.toLocaleString()} of your leads · {Math.round((a.leads / total) * 100)}%</span>
                     </span>
                     <span className={cn('text-[13px] font-semibold', on ? 'text-[var(--fr-blue)]' : 'text-[var(--fr-text-3)]')}>{on ? 'Selected' : 'Add'}</span>
                  </button>
               )
            })}
            {areaCodes.filter((c) => !LEAD_AREAS.slice(0, 3).some((a) => a.code === c)).map((c) => (
               <div key={c} className="flex items-center gap-4 px-5 min-h-[64px] bg-[var(--fr-blue-tint)]/60">
                  <span className="flex shrink-0 items-center justify-center w-10 h-10 rounded-full bg-[var(--fr-blue)] text-white"><Icon name="check" size={16} /></span>
                  <span className="flex-1 min-w-0">
                     <span className="block text-[15px] font-semibold">({c}) · {label(c)}</span>
                     <span className="block text-[13px] text-[var(--fr-text-2)]">Added by you</span>
                  </span>
                  <button type="button" onClick={() => toggle(c)} className="text-[13px] font-semibold text-[var(--fr-text-3)] hover:text-[var(--fr-text)]">Remove</button>
               </div>
            ))}
         </RowCard>
         <div className="mt-4" data-testid="area-editor">
            {adding ? (
               <SearchPick items={options} placeholder="Search by area code or city, e.g. 616 or Denver" emptyLabel="No area code or city matches" onPick={(code) => { toggle(code); setAdding(false) }} testId="area-search" />
            ) : (
               <div className="text-center">
                  <LinkButton onClick={() => setAdding(true)} testId="change-area">Search another area code or city</LinkButton>
               </div>
            )}
         </div>
      </Screen>
   )
}

/* ---------- 7. Your number ---------- */

export const NumberStep = () => {
   const { owner, calendar, next, direction } = useFirstRun()
   const [sent, setSent] = useState<'sent' | 'sending'>('sent')
   const again = () => {
      setSent('sending')
      setTimeout(() => setSent('sent'), 700)
   }
   useStepFooter('Saved, continue', next, { hint: 'Next: which leads we should leave alone.', testId: 'continue-number' })
   const vcard = `data:text/vcard;charset=utf-8,${encodeURIComponent(['BEGIN:VCARD', 'VERSION:3.0', 'FN:Forward Flow', `TEL;TYPE=CELL:${FF_NUMBER_E164}`, 'END:VCARD'].join('\n'))}`
   const tiles = [
      { icon: 'phone', tone: 'blue', title: 'Live transfers', body: `A lead wants to talk? We call ${owner.phone || 'your mobile'} and connect you.` },
      { icon: 'calendar', tone: 'green', title: 'Can’t pick up?', body: calendar ? `We book the appointment on your ${calendar === 'google' ? 'Google' : 'Outlook'} Calendar.` : 'We book the appointment into Follow Up Boss.' },
      { icon: 'message', tone: 'violet', title: 'Text it for help', body: 'How a campaign is doing, what a lead said, anything in your data.' },
   ] as const
   return (
      <Screen stepKey="number" direction={direction} kind="number" title="We texted you a number. Save it." lede={`It was sent to ${owner.phone || 'your mobile'} just now. Save it as “Forward Flow” so you recognise the call when a lead is on the line.`} width="md">
         <div className="rounded-[20px] border border-[var(--fr-border)] bg-white p-4">
            <div className="flex items-center gap-4">
               <span className="flex shrink-0 items-center justify-center w-12 h-12 rounded-full bg-[var(--fr-blue)] text-white"><Icon name="phone" size={20} /></span>
               <div className="flex-1 min-w-0">
                  <p className="text-[26px] leading-none font-semibold tracking-tight whitespace-nowrap" data-testid="ff-number">{FF_NUMBER}</p>
                  <p className="mt-1.5 text-[13px] text-[var(--fr-text-2)] truncate" data-testid="sent-line">Forward Flow · {sent === 'sent' ? `texted to ${owner.phone || 'your mobile'}` : 'sending the text'}</p>
               </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
               <a href={vcard} download="Forward Flow.vcf" className="fr-btn inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[var(--fr-surface-2)] text-[13px] font-semibold hover:bg-[#ebebef]"><Icon name="user" size={14} /> Save as a contact</a>
               <button type="button" onClick={again} className="inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[var(--fr-surface-2)] text-[13px] font-semibold hover:bg-[#ebebef]"><Icon name="message" size={14} /> Text it again</button>
            </div>
         </div>
         <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {tiles.map((t) => (
               <div key={t.title} className="rounded-2xl border border-[var(--fr-border)] bg-white p-3.5">
                  <span className={cn('inline-flex items-center justify-center w-8 h-8 rounded-full', t.tone === 'blue' && 'bg-[var(--fr-blue-tint)] text-[var(--fr-blue-dark)]', t.tone === 'green' && 'bg-[var(--fr-good-tint)] text-[var(--fr-good)]', t.tone === 'violet' && 'bg-[var(--fr-pond-tint)] text-[var(--fr-pond)]')}><Icon name={t.icon} size={14} /></span>
                  <p className="mt-2 text-[14px] font-semibold">{t.title}</p>
                  <p className="mt-0.5 text-[12px] leading-4 text-[var(--fr-text-2)]">{t.body}</p>
               </div>
            ))}
         </div>
      </Screen>
   )
}

/* ---------- 8a–c. Leads we leave alone, one question per screen ---------- */

const StopQuestion = ({ group, stepKey, index }: { group: TStopGroup; stepKey: string; index: number }) => {
   const { stops, setStops, next, direction } = useFirstRun()
   const q = STOP_QUESTIONS.find((x) => x.id === group)!
   const chosen = stops[group]
   const taken = new Set(Object.values(stops).flat())
   const suggestions = STOP_SUGGESTIONS[group].filter((id) => !taken.has(id))
   const byId = (id: string) => ALL_STAGES_AND_TAGS.find((x) => x.id === id)
   const options = ALL_STAGES_AND_TAGS.filter((x) => !taken.has(x.id)).map((x) => ({ id: x.id, label: x.name, kind: x.kind }))
   const last = index === 2
   useStepFooter(chosen.length ? (last ? 'Looks right, continue' : 'Next question') : 'Pick at least one to continue', next, {
      disabled: chosen.length === 0,
      hint: `Question ${index + 1} of 3 · Everything not listed here gets called. Editable later in Settings.`,
      testId: `confirm-${stepKey}`,
   })
   return (
      <Screen stepKey={stepKey} direction={direction} kind="stops" title={q.question} lede={q.hint} width="md">
         <div className="flex flex-wrap items-center justify-center gap-2" data-testid={`stop-${group}-chosen`}>
            {chosen.map((id) => {
               const it = byId(id)
               return it ? <Chip key={id} kind={it.kind} size="lg" onRemove={() => setStops(group, chosen.filter((x) => x !== id))}>{it.name}</Chip> : null
            })}
            {suggestions.map((id) => {
               const it = byId(id)
               return it ? <Chip key={id} kind={it.kind} size="lg" onAdd={() => setStops(group, [...chosen, id])}>{it.name}</Chip> : null
            })}
         </div>
         <div className="mt-5 max-w-[520px] mx-auto">
            <SearchPick items={options} placeholder="Search your stages and tags to add more" onPick={(id) => setStops(group, [...chosen, id])} testId={`stop-${group}-search`} />
         </div>
      </Screen>
   )
}
export const StopsWorkingStep = () => <StopQuestion group="working" stepKey="stops-working" index={0} />
export const StopsDoneStep = () => <StopQuestion group="done" stepKey="stops-done" index={1} />
export const StopsNeverStep = () => <StopQuestion group="never" stepKey="stops-never" index={2} />

/* ---------- 9. Texting approval: business · representative · address (three screens, two fields per row) ---------- */

interface IField {
   k: string
   label: string
   placeholder: string
   select?: { value: string; label: string }[]
   optional?: boolean
}
const BUSINESS_FIELDS: IField[] = [
   { k: 'legalBusinessName', label: 'Legal business name', placeholder: 'As registered with the state' },
   { k: 'friendlyBusinessName', label: 'Name leads will see', placeholder: 'Shown as the texting sender' },
   { k: 'websiteUrl', label: 'Website', placeholder: 'https://' },
   { k: 'businessType', label: 'Business type', placeholder: 'Choose one', select: [{ value: 'LLC', label: 'Limited Liability Corporation' }, { value: 'Corporation', label: 'Corporation' }, { value: 'Partnership', label: 'Partnership' }, { value: 'Sole', label: 'Sole proprietor' }] },
   { k: 'ein', label: 'EIN (tax ID)', placeholder: '12-3456789' },
   { k: 'businessEmail', label: 'Business email', placeholder: 'hello@brokerage.com' },
   { k: 'social', label: 'Social media profile (optional)', placeholder: 'A public page helps approval', optional: true },
]
const REP_FIELDS: IField[] = [
   { k: 'firstName', label: 'First name', placeholder: 'Your first name' },
   { k: 'lastName', label: 'Last name', placeholder: 'Your last name' },
   { k: 'jobPosition', label: 'Job position', placeholder: 'Choose one', select: [{ value: 'Owner', label: 'Owner' }, { value: 'CEO', label: 'CEO' }, { value: 'Director', label: 'Director' }, { value: 'Manager', label: 'Manager' }, { value: 'Agent', label: 'Agent' }, { value: 'Other', label: 'Other' }] },
   { k: 'businessTitle', label: 'Business title', placeholder: 'e.g. Broker / Owner' },
   { k: 'email', label: 'Email', placeholder: 'you@brokerage.com' },
   { k: 'phone', label: 'Mobile number', placeholder: '(719) 555-0100' },
]
const ADDRESS_FIELDS: IField[] = [
   { k: 'street', label: 'Street address', placeholder: '524 S Tejon St' },
   { k: 'city', label: 'City', placeholder: 'Colorado Springs' },
   { k: 'region', label: 'State', placeholder: 'Choose a state', select: [{ value: 'CO', label: 'Colorado' }, { value: 'CA', label: 'California' }, { value: 'TX', label: 'Texas' }, { value: 'FL', label: 'Florida' }, { value: 'NY', label: 'New York' }] },
   { k: 'zip', label: 'ZIP', placeholder: '80903' },
   { k: 'businessPhone', label: 'Business phone', placeholder: '(719) 555-0100' },
]
const ALL_BUSINESS_FIELDS = [...BUSINESS_FIELDS, ...REP_FIELDS, ...ADDRESS_FIELDS]

/** Two fields per row, each with a label above it. */
const FormGrid = ({ fields, testId }: { fields: IField[]; testId: string }) => {
   const { business, setBusiness } = useFirstRun()
   return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3" data-testid={testId}>
         {fields.map((f) =>
            f.select ? (
               <Select key={f.k} label={f.label} value={business[f.k] || ''} onChange={(v) => setBusiness(f.k, v)} options={f.select} placeholder={f.placeholder} testId={`field-${f.k}`} />
            ) : (
               <Input key={f.k} label={f.label} value={business[f.k] || ''} onChange={(v) => setBusiness(f.k, v)} placeholder={f.placeholder} testId={`field-${f.k}`} />
            )
         )}
      </div>
   )
}

const missingOf = (fields: IField[], business: Record<string, string>) => fields.filter((f) => !f.optional && !(business[f.k] || '').trim())

const useFinish = () => {
   const navigate = useNavigate()
   const { set } = useFirstRun()
   return () => {
      set('setupDone', true)
      navigate('/start/home')
   }
}

const SECTIONS: { title: string; blurb: string; fields: IField[]; testId: string }[] = [
   { title: 'The business', blurb: 'As registered with the state.', fields: BUSINESS_FIELDS, testId: 'business-form' },
   { title: 'The person behind it', blurb: 'The authorised representative the carriers can contact. Not shown to leads.', fields: REP_FIELDS, testId: 'representative-form' },
   { title: 'The registered address', blurb: 'Matched against public records.', fields: ADDRESS_FIELDS, testId: 'address-form' },
]

/** One page, three sections, two fields per row. This screen scrolls. */
export const BusinessStep = () => {
   const { owner, email, business, setBusiness, set, direction } = useFirstRun()
   const finish = useFinish()
   const [sending, setSending] = useState(false)
   useEffect(() => {
      const [first, ...rest] = owner.name.split(' ')
      const pre: Record<string, string> = { ...DEMO_BUSINESS, firstName: first, lastName: rest.join(' '), email: email || owner.email, phone: owner.phone || '', jobPosition: 'Owner', businessTitle: 'Broker / Owner', businessPhone: owner.phone || '', businessEmail: email || owner.email }
      Object.entries(pre).forEach(([k, v]) => {
         if (!business[k] && v) setBusiness(k, v)
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])
   const missing = missingOf(ALL_BUSINESS_FIELDS, business)
   const prefilled = ALL_BUSINESS_FIELDS.filter((f) => (business[f.k] || '').trim()).length
   const send = () => {
      setSending(true)
      setTimeout(() => {
         set('businessSent', true)
         finish()
      }, 900)
   }
   useStepFooter(missing.length ? `${missing.length} ${missing.length === 1 ? 'field' : 'fields'} left to send for approval` : 'Send for approval and finish', send, {
      disabled: missing.length > 0,
      loading: sending,
      hint: `${prefilled} of ${ALL_BUSINESS_FIELDS.length} filled from your account · approval takes 3 to 5 days, calls start today.`,
      testId: 'send-business',
   })
   return (
      <Screen stepKey="business" direction={direction} kind="business" title="Confirm your business details" lede="Phone carriers only allow texting from a business they can identify. We filled in what we know. Complete the blanks and we file it for you." width="md">
         <div className="space-y-7" data-testid="a2p-form">
            {SECTIONS.map((sec, i) => (
               <section key={sec.testId}>
                  <div className="flex items-baseline gap-3 mb-3">
                     <span className="flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-[var(--fr-blue)] text-white text-[12px] font-semibold">{i + 1}</span>
                     <h2 className="text-[16px] font-semibold">{sec.title}</h2>
                     <span className="text-[13px] text-[var(--fr-text-3)]">{sec.blurb}</span>
                  </div>
                  <FormGrid fields={sec.fields} testId={sec.testId} />
               </section>
            ))}
         </div>
      </Screen>
   )
}
